import React from 'react';

import { arrayOf } from '@nfctron/nexus-core';

import { Center, PasswordInput as MantinePasswordInput, Progress, Text } from '@mantine/core';
import { CheckIcon, XIcon } from 'lucide-react';

import { getTextFieldErrorComponent } from '@root/features/form/components/TextInput/TextInput';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type Component, type ComponentDisplayName } from '@root/lib/types/components';

import { type PasswordInputProps } from './PasswordInput.types';

/**
 * Password requirement list
 * @export
 */
export const PASSWORD_REQUIREMENTS = [
	{ re: /[0-9]/, type: 'number' },
	{ re: /[a-z]/, type: 'lowercase' },
	{ re: /[A-Z]/, type: 'uppercase' },
];

/**
 * Password minimum lnength
 * @type {number}
 * @export
 */
export const MIN_PASSWORD_LENGTH = 6;

/**
 * Returns password strength on the scale of 0-100
 * @param {string} password
 * @returns {number}
 * @export
 */
export const getPasswordStrength = (password: string) => {
	let multiplier = password.length > MIN_PASSWORD_LENGTH ? 0 : 1;

	PASSWORD_REQUIREMENTS.forEach((requirement) => {
		if (!requirement.re.test(password)) {
			multiplier += 1;
		}
	});

	return Math.max(100 - (100 / (PASSWORD_REQUIREMENTS.length + 1)) * multiplier, 0);
};

/**
 * PasswordInput component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const PasswordInput: Component<PasswordInputProps> = ({ error, value = '', withRequirements, ...rest }) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON, PasswordInput.displayName);

	/** password input node */
	const passwordInputNode = <MantinePasswordInput error={getTextFieldErrorComponent(error)} value={value} {...rest} />;

	if (withRequirements) {
		const strength = getPasswordStrength(value);
		return (
			<div className="flex flex-col gap-2">
				{/* input */}
				{passwordInputNode}
				{/* bars */}
				<div className="grid auto-cols-fr grid-flow-col gap-3">
					{arrayOf(4).map((key, index, arr) => (
						<Progress
							styles={{ section: { transitionDuration: '0ms' } }}
							value={value.length > 0 && index === 0 ? 100 : strength >= ((index + 1) / arr.length) * 100 ? 100 : 0}
							color={strength > 80 ? 'green' : strength > 50 ? 'amber' : 'red'}
							key={index}
							size={arr.length}
						/>
					))}
				</div>
				{/* checks */}
				<div className="mt-1">
					{PASSWORD_REQUIREMENTS.map((requirement, index) => {
						const meets = requirement.re.test(value);
						return (
							<Text key={requirement.type} c={meets ? 'green' : 'red'} size="md">
								<Center inline>
									{meets ? <CheckIcon className="size-5" /> : <XIcon className="size-5" />}
									<span className="ml-3">{t(`requirement.${requirement.type}.text`)}</span>
								</Center>
							</Text>
						);
					})}
				</div>
			</div>
		);
	}

	return passwordInputNode;
};

/**
 * PasswordInput component display name
 * @type {ComponentDisplayName}
 */
PasswordInput.displayName = 'component/PasswordInput';
